import type { FC } from 'react';
import { useRouter } from 'next/router';

import {
  Box,
  Link,
  type LinkProps,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
} from '@packages/shared';
import { Checkmark32, Cancel32 } from '@packages/themes/icons';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useMouseflow } from '@packages/tracking';

export type CategoryFilterItem = Omit<CategoryItemProps, 'onClick'> & {
  id: string;
};

export type CategoryFilterStandaloneProps = {
  /** The current category tree, flattened and sorted by selection */
  categories: CategoryFilterItem[];
  /** If `true`, shows a dedicated "clear all" interaction/button in addition to the root level category */
  showClearAll?: boolean;
  /** Callback fired when a category is clicked or all categories are cleared */
  onNavigate?: () => void;
};

type CategoryItemProps = {
  /** name of the category */
  name: string;
  /** hierarchical level in the category tree, translates to indendation */
  level: number;
  /** count of total items in the current search result that match this category */
  count?: number;
  /** Is the item currently selected, wether indirectly (child is selected) or directly */
  isSelected?: boolean;
  /** Is the item currently **directly** selected (not by hierarchical parentage) */
  isSelectedExact?: boolean;
  /** A callback that is invoked when the item is clicked */
  onClick?: () => void;
} & Pick<LinkProps, 'href'>;

/** A single category item for the list
 *
 * Indents itself based on the level of the category.
 *
 * Different styling is applied for parent nodes and selected nodes.
 */
const CategoryItem: FC<CategoryItemProps> = ({
  name,
  level,
  count,
  href,
  isSelected,
  isSelectedExact,
  onClick,
}) => {
  const pushMouseflowData = useMouseflow();

  return (
    <Link
      href={href}
      color="text.dark"
      underline="none"
      onClick={() => {
        pushMouseflowData('search_FilterLeft');
        onClick?.();
      }}
    >
      <ListItemButton divider>
        <ListItemText sx={{ paddingLeft: level * 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: isSelected ? 'bold' : 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
        </ListItemText>
        {isSelectedExact && <Checkmark32 sx={{ color: 'primary.main' }} />}
        {!isSelected && count && count > 0 && <Chip label={count} sx={{ height: '1.5rem' }} />}
      </ListItemButton>
    </Link>
  );
};

const messages = defineMessages({
  clearSelection: {
    id: 'search.filters.category.clearSelection',
    defaultMessage: 'Auswahl aufheben',
  },
});

/**
 * Displays a single level of categories as navigation items, including upwards if possible.
 * */
export const CategoryFilterStandalone: FC<CategoryFilterStandaloneProps> = ({
  categories = [],
  showClearAll,
  onNavigate,
}) => {
  const {
    pathname,
    query: { c, ...otherQueryParameters },
  } = useRouter();

  const root = { ...categories[0] };
  root.href = { pathname, query: otherQueryParameters };

  const canClear = categories.some((category) => category.isSelected);

  return (
    <Box sx={{ overflow: 'auto' }}>
      <List>
        {categories.map((category) => (
          <CategoryItem key={category.id} {...category} onClick={onNavigate} />
        ))}

        {showClearAll && canClear && (
          <Link href={root.href} color="text.dark" underline="none" onClick={onNavigate}>
            <ListItemButton divider sx={{ backgroundColor: 'grey.light' }}>
              <ListItemText>
                <Typography variant="body2">
                  <FormattedMessage {...messages.clearSelection} />
                </Typography>
              </ListItemText>
              <Cancel32 sx={{ color: 'text.dark' }} />
            </ListItemButton>
          </Link>
        )}
      </List>
    </Box>
  );
};
